import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  Users,
  ArrowUpRight,
  GitGraph,
  Network,
  ShieldCheck,
  Settings
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { OrgChartNodeData } from '../../types/org-chart';

interface DetailsPanelProps {
  selectedNode: OrgChartNodeData | null;
  selectedEdge: { id: string; source: string; target: string; type?: string } | null;
  sourceNode: OrgChartNodeData | null;
  targetNode: OrgChartNodeData | null;
  isEditMode?: boolean;
  onClose: () => void;
  reportingChain: OrgChartNodeData[];
  directReports: OrgChartNodeData[];
  onSelectNode: (id: string) => void;
  onRemoveEdge: (targetId: string, type: 'reporting' | 'functional') => void;
  pendingConnection: { sourceId: string; targetId: string } | null;
  onConfirmAssignment: (type: 'reporting' | 'functional') => void;
  isReassigning?: boolean;
  reportingManager: OrgChartNodeData | null;
  functionalManager: OrgChartNodeData | null;
  onFocusNode?: (id: string) => void;
}

const DetailsPanel = ({
  selectedNode,
  selectedEdge,
  sourceNode,
  targetNode,
  isEditMode,
  onClose,
  reportingChain,
  directReports,
  onSelectNode,
  onRemoveEdge,
  pendingConnection,
  onConfirmAssignment,
  isReassigning,
  reportingManager,
  functionalManager,
  onFocusNode
}: DetailsPanelProps) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [selectedNode?.id]);
  return (
    <AnimatePresence>
      {(selectedNode || selectedEdge || pendingConnection) && (
        <>
          {/* Backdrop for mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/5 backdrop-blur-[2px] z-[60] lg:hidden"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-24 right-6 bottom-8 w-96 bg-white shadow-2xl rounded-3xl z-[70] flex flex-col border border-gray-100"
          >
            {/* Header */}
            <div className="p-6 pb-4 flex items-center justify-between border-b border-gray-50">
              <div className="flex items-center gap-2 text-gray-400">
                {selectedNode ? <User size={18} /> : <GitGraph size={18} />}
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {selectedNode ? 'Employee Profile' : pendingConnection ? 'Propose Assignment' : 'Connection Details'}
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {selectedNode ? (
                <>
                  {/* Compact Profile Header - Increased Breathing Room */}
                  <div className="p-6 flex items-center gap-6 border-b border-gray-50 bg-slate-50/50">
                    <div className="relative shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-white overflow-hidden ring-4 ring-primary-50/30 shadow-sm border border-primary-100">
                        {selectedNode.profilePic && !imgError ? (
                          <img
                            src={selectedNode.profilePic}
                            alt={selectedNode.fullName}
                            className="w-full h-full object-cover"
                            onError={() => setImgError(true)}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-primary-600 font-bold text-xl bg-slate-50">
                            {selectedNode.fullName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${{
                          active: 'bg-emerald-500',
                          on_leave: 'bg-amber-500',
                          offline: 'bg-slate-400',
                          inactive: 'bg-rose-500',
                          terminated: 'bg-slate-500',
                        }[selectedNode.status || 'active'] || 'bg-slate-400'
                        }`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-gray-900 truncate mb-0.5">{selectedNode.fullName}</h2>
                      <p className="text-xs font-bold text-primary-600 mb-2 uppercase tracking-widest">{selectedNode.designation}</p>
                      <div className="flex gap-1.5">
                        <span className="px-2 py-0.5 bg-white border border-gray-100 rounded-md text-[9px] font-bold text-gray-400 uppercase tracking-tight">{selectedNode.department}</span>
                        <span className="px-2 py-0.5 bg-primary-50 border border-primary-100 rounded-md text-[9px] font-bold text-primary-600 uppercase tracking-tight">Full-Time</span>
                      </div>
                    </div>
                  </div>

                  {/* Stacked Managers Section */}
                  <div className="p-6 border-b border-gray-50 bg-slate-50/20">
                    <div className="space-y-4">
                      {/* Reporting Manager Section */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md border border-blue-100 w-fit">
                          <ShieldCheck size={10} />
                          <span className="text-[9px] font-bold uppercase tracking-widest">Reporting Manager</span>
                        </div>
                        {reportingManager ? (
                          <button
                            onClick={() => onSelectNode(reportingManager.id)}
                            className="flex items-center gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all text-left group"
                          >
                            <div className="relative shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-slate-50 overflow-hidden border border-slate-100 flex items-center justify-center">
                                {reportingManager.profilePic && !imgError ? (
                                  <img src={reportingManager.profilePic} className="w-full h-full object-cover" onError={() => setImgError(true)} />
                                ) : (
                                  <div className="text-primary-600 font-bold text-sm">
                                    {reportingManager.fullName?.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm ${{
                                  active: 'bg-emerald-500',
                                  on_leave: 'bg-amber-500',
                                  offline: 'bg-slate-400',
                                  inactive: 'bg-rose-500',
                                  terminated: 'bg-slate-500',
                                }[reportingManager.status || 'active'] || 'bg-slate-400'
                                }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 truncate">{reportingManager.fullName}</p>
                              <p className="text-[10px] text-gray-500 truncate font-medium uppercase tracking-tight">{reportingManager.designation}</p>
                            </div>
                            <ArrowUpRight size={16} className="text-gray-300 group-hover:text-primary-500 transition-all" />
                          </button>
                        ) : (
                          <div className="p-3 bg-white/50 rounded-xl border border-dashed border-gray-200 text-center">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest italic">None Assigned</span>
                          </div>
                        )}
                      </div>

                      {/* Functional Manager Section */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md border border-emerald-100 w-fit">
                          <Settings size={10} />
                          <span className="text-[9px] font-bold uppercase tracking-widest">Functional Manager</span>
                        </div>
                        {functionalManager ? (
                          <button
                            onClick={() => onSelectNode(functionalManager.id)}
                            className="flex items-center gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:border-emerald-200 hover:shadow-sm transition-all text-left group"
                          >
                            <div className="relative shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-slate-50 overflow-hidden border border-slate-100 flex items-center justify-center">
                                {functionalManager.profilePic && !imgError ? (
                                  <img src={functionalManager.profilePic} className="w-full h-full object-cover" onError={() => setImgError(true)} />
                                ) : (
                                  <div className="text-emerald-600 font-bold text-sm">
                                    {functionalManager.fullName?.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm ${{
                                  active: 'bg-emerald-500',
                                  on_leave: 'bg-amber-500',
                                  offline: 'bg-slate-400',
                                  inactive: 'bg-rose-500',
                                  terminated: 'bg-slate-500',
                                }[functionalManager.status || 'active'] || 'bg-slate-400'
                                }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 truncate">{functionalManager.fullName}</p>
                              <p className="text-[10px] text-gray-500 truncate font-medium uppercase tracking-tight">{functionalManager.designation}</p>
                            </div>
                            <ArrowUpRight size={16} className="text-emerald-400 group-hover:text-emerald-600 transition-all" />
                          </button>
                        ) : (
                          <div className="p-3 bg-white/50 rounded-xl border border-dashed border-gray-200 text-center">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest italic">None Assigned</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Direct Reports */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-primary-500" />
                        <h3 className="text-sm font-bold text-gray-900">Direct Reports</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                        {directReports.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {directReports.map((report) => (
                        <button
                          key={report.id}
                          onClick={() => onSelectNode(report.id)}
                          className="flex items-center gap-3 p-2 rounded-xl bg-gray-50/50 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-100 transition-all group text-left"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-50 overflow-hidden shrink-0 border border-slate-100 flex items-center justify-center">
                            {report.profilePic && !imgError ? (
                              <img
                                src={report.profilePic}
                                className="w-full h-full object-cover"
                                onError={() => setImgError(true)}
                              />
                            ) : (
                              <div className="text-slate-600 font-bold text-xs">
                                {report.fullName?.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate mb-1">{report.fullName}</p>
                            <p className="text-[10px] text-gray-500 truncate">{report.designation}</p>
                          </div>
                          <ChevronRight size={14} className="text-gray-300 group-hover:text-primary-500 transition-all" />
                        </button>
                      ))}
                      {directReports.length === 0 && (
                        <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-2xl">
                          <p className="text-xs text-gray-400">No direct reports</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-6 space-y-8">
                  <div className="flex flex-col items-center gap-6">
                    <div className="flex items-center justify-center gap-4 w-full">
                      {/* Source */}
                      <div className="flex flex-col items-center gap-2 flex-1">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center text-primary-600 font-bold text-xl shrink-0 border border-slate-100 shadow-sm">
                          {sourceNode?.profilePic && !imgError ? (
                            <img
                              src={sourceNode.profilePic}
                              className="w-full h-full object-cover"
                              onError={() => setImgError(true)}
                            />
                          ) : (
                            sourceNode?.fullName?.charAt(0) || <User size={32} className="text-gray-300" />
                          )}
                        </div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-center">Manager</p>
                        <p className="text-xs font-bold text-gray-900 text-center line-clamp-1">{sourceNode?.fullName}</p>
                      </div>

                      <div className="flex flex-col items-center gap-1">
                        <ArrowUpRight size={20} className="text-primary-400 rotate-45" />
                        <span className="text-[9px] font-bold text-primary-500 uppercase tracking-tighter bg-primary-50 px-2 py-0.5 rounded-full">
                          {pendingConnection ? 'Pending' : (targetNode?.functionalManagerId === sourceNode?.id && targetNode?.reportingManagerId !== sourceNode?.id ? 'Functional' : 'Reporting')}
                        </span>
                      </div>

                      {/* Target */}
                      <div className="flex flex-col items-center gap-2 flex-1">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center text-primary-600 font-bold text-xl shrink-0 border border-slate-100 shadow-sm">
                          {targetNode?.profilePic && !imgError ? (
                            <img
                              src={targetNode.profilePic}
                              className="w-full h-full object-cover"
                              onError={() => setImgError(true)}
                            />
                          ) : (
                            targetNode?.fullName?.charAt(0) || <User size={32} className="text-gray-300" />
                          )}
                        </div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-center">Direct Report</p>
                        <p className="text-xs font-bold text-gray-900 text-center line-clamp-1">{targetNode?.fullName}</p>
                      </div>
                    </div>

                    <div className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        {pendingConnection ? 'Proposing Relationship' : 'Connection Type'}
                      </h4>
                      <p className="text-sm text-slate-700 font-medium leading-relaxed">
                        {pendingConnection ? (
                          <>You are assigning <span className="font-bold">{sourceNode?.fullName}</span> as the manager for <span className="font-bold">{targetNode?.fullName}</span>. Please choose the relationship type below.</>
                        ) : (
                          <>This is a <span className="font-bold text-primary-600">
                            {targetNode?.functionalManagerId === sourceNode?.id && targetNode?.reportingManagerId !== sourceNode?.id ? 'Functional' : 'Reporting'}
                          </span> relationship where <span className="font-bold">{sourceNode?.fullName}</span> manages <span className="font-bold">{targetNode?.fullName}</span>.</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50/50 border-t border-gray-100 rounded-b-3xl space-y-3">
              {selectedNode && !pendingConnection && !selectedEdge ? (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => onFocusNode?.(selectedNode.id)}
                    className="w-full py-3 bg-white border border-primary-200 text-primary-600 rounded-xl text-sm font-bold shadow-sm hover:bg-primary-50 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Network size={16} />
                    Focus Hierarchy
                  </button>
                </div>
              ) : pendingConnection ? (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => onConfirmAssignment('reporting')}
                    disabled={isReassigning}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Assign as Reporting Manager
                  </button>
                  <button
                    onClick={() => onConfirmAssignment('functional')}
                    disabled={isReassigning}
                    className="w-full py-3 bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Assign as Functional Manager
                  </button>
                </div>
              ) : (
                isEditMode && (
                  <button
                    onClick={() => {
                      if (targetNode) {
                        const type = targetNode.functionalManagerId === sourceNode?.id && targetNode.reportingManagerId !== sourceNode?.id ? 'functional' : 'reporting';
                        onRemoveEdge(targetNode.id, type);
                      }
                    }}
                    disabled={isReassigning}
                    className="w-full py-3 bg-red-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-red-200 hover:bg-red-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Remove Link
                  </button>
                )
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DetailsPanel;
